import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  ShieldOff,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function fmtDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

export default function AccountConsents() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [code, setCode] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [granting, setGranting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users/me/consents");
      setItems(data.consents || []);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to load your consents.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onPreview = async (e) => {
    e?.preventDefault?.();
    if (!code.trim()) {
      toast.error("Paste the partner code first.");
      return;
    }
    setPreviewing(true);
    try {
      const { data } = await api.post("/users/me/consents/preview", {
        key_prefix: code.trim(),
      });
      setPreviewData(data);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Couldn't find that partner.",
      );
    } finally {
      setPreviewing(false);
    }
  };

  const onGrant = async () => {
    if (!previewData) return;
    setGranting(true);
    try {
      await api.post("/users/me/consents", { key_id: previewData.key_id });
      toast.success(`Access granted to ${previewData.partner_org || previewData.partner_name}`);
      setPreviewData(null);
      setCode("");
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to grant consent.",
      );
    } finally {
      setGranting(false);
    }
  };

  const onRevoke = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Revoke ${row.partner_org || row.partner_name}'s access to your career report? They won't be able to read it after this.`,
    )) return;
    try {
      await api.delete(`/users/me/consents/${row.key_id}`);
      toast.success("Access revoked");
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to revoke.",
      );
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="account-consents-page">
      <Navbar />
      <main className="pt-24 lg:pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <Link
            to="/account"
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
            data-testid="account-consents-back-link"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Back to account
          </Link>
          <h1
            className="mt-6 text-3xl lg:text-4xl font-medium tracking-tight text-slate-900"
            data-testid="account-consents-heading"
          >
            Partner access
          </h1>
          <p className="mt-2 text-base text-slate-600 max-w-2xl">
            Some partner companies — usually the HR team helping you through
            redundancy — can read your career report via our API so they can
            recommend roles directly inside their own portal. You're in
            control: grant access by partner code, revoke it any time.
          </p>

          {/* Grant new */}
          <section
            className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_4px_18px_rgb(0,0,0,0.03)]"
            data-testid="account-consents-grant-card"
          >
            <h2 className="text-base font-medium text-slate-900">Grant access to a partner</h2>
            <p className="mt-1 text-sm text-slate-500">
              Your partner will give you a code that starts with{" "}
              <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">tamj_live_</code>.
              Paste it below and confirm.
            </p>
            <form onSubmit={onPreview} className="mt-4 flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="tamj_live_AbCdEf12"
                className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-mono focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                data-testid="account-consents-code-input"
              />
              <button
                type="submit"
                disabled={previewing}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                data-testid="account-consents-preview-button"
              >
                {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                Look up partner
              </button>
            </form>

            {previewData && (
              <div
                className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50/50 p-4"
                data-testid="account-consents-preview"
              >
                {previewData.already_granted ? (
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-4 w-4 text-emerald-700 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-emerald-900">
                        You've already granted access to {previewData.partner_org || previewData.partner_name}.
                      </p>
                      <p className="mt-1 text-xs text-emerald-800">
                        Scroll down to revoke it if you'd like to remove access.
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-slate-700">
                      <strong>{previewData.partner_org || previewData.partner_name}</strong>{" "}
                      would like to read your career report.
                    </p>
                    {previewData.contact_email && (
                      <p className="mt-1 text-xs text-slate-500">
                        Partner contact: {previewData.contact_email}
                      </p>
                    )}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={onGrant}
                        disabled={granting}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                        data-testid="account-consents-grant-button"
                      >
                        {granting ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
                        Yes, grant access
                      </button>
                      <button
                        type="button"
                        onClick={() => setPreviewData(null)}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                        data-testid="account-consents-cancel-button"
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </section>

          {/* Current consents */}
          <section className="mt-10" data-testid="account-consents-list">
            <h2 className="text-base font-medium text-slate-900">
              Active partner access
            </h2>
            {loading ? (
              <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : items.length === 0 ? (
              <div
                className="mt-4 rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500"
                data-testid="account-consents-empty"
              >
                You haven't granted any partner access yet.
              </div>
            ) : (
              <ul className="mt-4 space-y-3">
                {items.map((c) => (
                  <li
                    key={c.key_id}
                    className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                    data-testid={`account-consents-row-${c.key_id}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-900 truncate">
                          {c.partner_org || c.partner_name || "Partner"}
                        </span>
                        {c.revoked ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
                            <ShieldOff className="h-3 w-3" /> Revoked
                          </span>
                        ) : !c.key_active ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] text-amber-700">
                            <AlertTriangle className="h-3 w-3" /> Key inactive
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] text-emerald-700">
                            <CheckCircle2 className="h-3 w-3" /> Active
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-slate-500">
                        Granted {fmtDateTime(c.granted_at)}
                        {c.revoked && c.revoked_at
                          ? ` · revoked ${fmtDateTime(c.revoked_at)}`
                          : ""}
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-400 font-mono">
                        {c.key_prefix || c.key_id}
                      </div>
                    </div>
                    {!c.revoked && (
                      <button
                        type="button"
                        onClick={() => onRevoke(c)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50"
                        data-testid={`account-consents-revoke-${c.key_id}`}
                      >
                        <Trash2 className="h-3 w-3" /> Revoke
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
